import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  useComplianceCheckResults,
  useSaveComplianceEvaluation,
} from "@/hooks/compliance/compliance.hook";
import { toast } from "@/utils/toast";
import { PencomRoutes } from "@/router/PencomRoutes";
import { useAuthStore } from "@/stores/authStore";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { useForm } from "@tanstack/react-form";
import PencomFileUpload from "@/components/input/PencomFileUpload";
import PencomTextField from "@/components/input/PencomTextField";
import { useState, useRef } from "react";
import { useFileUpload } from "@/hooks/file-upload/file-upload.hook";
import { EStorage } from "@/constants/enums/storage.enum";
import type { BaseResponse } from "@/models/responses/base/baseResponse.response";
import type { ComplianceEvaluation } from "@/models/responses/compliance/complianceEvaluation.response";

export const PCCEvaluationResult = ({
  complianceEvaluationData,
}: {
  complianceEvaluationData?:
    | ComplianceEvaluation
    | BaseResponse<ComplianceEvaluation>;
}) => {
  const { currentEmployer } = useAuthStore();
  const navigate = useNavigate();
  const {
    mutate: saveComplianceEvaluation,
    isPending: isSavingComplianceEvaluation,
  } = useSaveComplianceEvaluation();
  const { mutate: uploadFile, isPending: isUploadingFiles } = useFileUpload();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentUrls, setDocumentUrls] = useState<string[]>([]);
  const [uploadingFileIndex, setUploadingFileIndex] = useState<number | null>(
    null
  );
  const [documentsTouched, setDocumentsTouched] = useState(false);
  const fileUrlMapRef = useRef<Map<string, string>>(new Map());

  const {
    checkResults,
    canProceed,
    hasSoftBlock,
    hasOutstandingPenaltySoftBlock,
    hasMissingContributionsSoftBlock,
  } = useComplianceCheckResults(complianceEvaluationData);

  const headingText = !canProceed
    ? "Compliance Review Incomplete"
    : hasSoftBlock
    ? hasOutstandingPenaltySoftBlock
      ? "Please explain the issue below and attach supporting documents."
      : "Please explain the issue below and attach supporting documents (optional)."
    : "All checks completed - you are good to go!";

  const descriptionText = !canProceed
    ? "You need to resolve the item below before you can apply for your PCC."
    : hasSoftBlock
    ? ""
    : "Your organisation meets all the required compliance conditions. You can now proceed to submit your PenCom Compliance Certificate (PCC) application.";

  const getFileKey = (file: File) =>
    `${file.name}-${file.size}-${file.lastModified}`;

  const handleFilesSelect = (files: File[]) => {
    setDocumentsTouched(true);
    setSelectedFiles(files);

    const newFiles = files.filter((file) => {
      const fileKey = getFileKey(file);
      return !fileUrlMapRef.current.has(fileKey);
    });

    if (newFiles.length === 0) {
      const updatedUrls = files
        .map((f) => {
          const key = getFileKey(f);
          return fileUrlMapRef.current.get(key) || "";
        })
        .filter((url) => url !== "");
      setDocumentUrls(updatedUrls);
      return;
    }

    setUploadingFileIndex(0);
    uploadFile(
      {
        file: newFiles.length === 1 ? newFiles[0] : newFiles,
        path: EStorage.COMPLIANCE,
        document_type: EStorage.COMPLIANCE,
      },
      {
        onSuccess: (
          response:
            | {
                data?: { urls?: string[]; url?: string };
                urls?: string[];
                url?: string;
              }
            | string[]
        ) => {
          toast.success("File(s) Uploaded Successfully");
          let urls: string[] = [];

          if (newFiles.length === 1) {
            const url = Array.isArray(response)
              ? response[0]
              : response?.url
              ? response.url
              : response?.data?.url
              ? response.data.url
              : "";
            if (url) urls = [url];
          } else {
            urls = Array.isArray(response)
              ? response
              : response?.urls
              ? response.urls
              : response?.data?.urls
              ? response.data.urls
              : [];
          }

          newFiles.forEach((file, index) => {
            const fileKey = getFileKey(file);
            if (urls[index]) {
              fileUrlMapRef.current.set(fileKey, urls[index]);
            }
          });

          const updatedUrls = files
            .map((f) => {
              const key = getFileKey(f);
              return fileUrlMapRef.current.get(key) || "";
            })
            .filter((url) => url !== "");

          setDocumentUrls(updatedUrls);
          setUploadingFileIndex(null);
        },
        onError: (error) => {
          console.error(error);
          setUploadingFileIndex(null);
          newFiles.forEach((file) => {
            const fileKey = getFileKey(file);
            fileUrlMapRef.current.delete(fileKey);
            setSelectedFiles((prev) =>
              prev.filter((f) => getFileKey(f) !== fileKey)
            );
          });
        },
      }
    );
  };

  const handleFileRemoveAt = (index: number) => {
    setDocumentsTouched(true);
    const fileToRemove = selectedFiles[index];
    if (fileToRemove) {
      const fileKey = getFileKey(fileToRemove);
      fileUrlMapRef.current.delete(fileKey);
    }

    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setDocumentUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const form = useForm({
    defaultValues: {
      comment: "",
    },
    onSubmit: async ({ value }) => {
      // A comment is mandatory whenever any soft block is present; documents
      // are only mandatory for the outstanding-penalty soft block.
      if (hasSoftBlock && (!value.comment || value.comment.length < 10)) {
        toast.error("Comment is required (at least 10 characters).");
        return;
      }
      if (hasOutstandingPenaltySoftBlock && documentUrls.length === 0) {
        toast.error("Please upload at least one supporting document.");
        return;
      }

      saveComplianceEvaluation(
        {
          employer_code: currentEmployer?.employerCode ?? "",
          comment: value.comment || undefined,
          documents: hasOutstandingPenaltySoftBlock
            ? documentUrls
            : documentUrls.length > 0
            ? documentUrls
            : undefined,
        },
        {
          onSuccess: () => {
            toast.success("Application submitted successfully");
            navigate(PencomRoutes.PCC_APPLICATION_SUCCESSFUL, {
              replace: true,
            });
          },
          onError: (error) => {
            const err = error as AxiosError<{ message?: string }>;
            toast.error(
              err.response?.data?.message || "Failed to submit application"
            );
          },
        }
      );
    },
  });

  return (
    <div className="w-full flex flex-col h-full  max-w-[600px] mx-auto mt-8">
      <div className="flex flex-col gap-y-4">
        <p className="font-clash-display text-2xl text-left font-regular leading-[100%] tracking-[0%] text-medium-dark-grey">
          {headingText}
        </p>
        <p className="font-clash-display text-center font-regular text-sm leading-[18px] tracking-[-0.5%] text-medium-dark-grey">
          {descriptionText}
        </p>
      </div>
      <div className="flex flex-col mt-8 justify-center gap-y-6">
        {checkResults.map((check, index) => (
          <div key={index} className="flex flex-row gap-x-4 items-center">
            <img
              src={check.icon}
              alt={
                check.status === "passed"
                  ? "check"
                  : check.status === "warning"
                  ? "warning"
                  : "failed"
              }
              className="w-6 h-6"
            />
            <p className="font-clash-display text-[16px] font-medium leading-[100%] tracking-[1.5%] text-medium-dark-grey">
              {check.name}
            </p>
          </div>
        ))}
      </div>
      {hasMissingContributionsSoftBlock && (
        <div className="mt-4 bg-baby-blue border border-navy-blue rounded-lg px-4 py-3">
          <p className="font-clash-display text-sm font-regular leading-[18px] text-medium-dark-grey">
            If you have Pension Clearance Certificates for the missing
            contribution years, kindly upload them along with your comments.
          </p>
        </div>
      )}
      {hasSoftBlock && (
        <div className="flex flex-col mt-8 gap-y-6">
          <form.Field
            name="comment"
            validators={{
              onChange: ({ value }) => {
                if (!hasSoftBlock) return undefined;
                if (!value) return "Comment is required.";
                if (value.length < 10)
                  return "Comment must be at least 10 characters long.";
                return undefined;
              },
            }}
            children={(field) => {
              const wordCount =
                field.state.value.trim() === ""
                  ? 0
                  : field.state.value
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0).length;

              return (
                <div className="flex-1">
                  <PencomTextField
                    id="comment"
                    label="Comment *"
                    placeholder="Provide an explanation here"
                    value={field.state.value}
                    onTextareaChange={(value) =>
                      field.handleChange(value.target.value)
                    }
                    type="text"
                    multiline={true}
                    rows={4}
                    className="min-h-[100px]"
                  />
                  <div className="flex justify-between items-center mt-1">
                    {field.state.meta.errors.length > 0 ? (
                      <p className="text-red-500 text-xs">
                        {field.state.meta.errors[0]}
                      </p>
                    ) : (
                      <div />
                    )}
                    <p className="text-medium-dark-grey text-xs font-clash-display">
                      {wordCount} {wordCount === 1 ? "word" : "words"}
                    </p>
                  </div>
                </div>
              );
            }}
          />
          <PencomFileUpload
            id="soft-block-upload"
            label={
              hasOutstandingPenaltySoftBlock
                ? "Add Supporting Documents *"
                : "Add Supporting Documents"
            }
            placeholder="Click here or drag and drop to upload"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={10}
            maxFiles={5}
            onFilesSelect={handleFilesSelect}
            selectedFiles={selectedFiles}
            className=""
            disabled={isUploadingFiles || uploadingFileIndex !== null}
            onFileRemoveAt={handleFileRemoveAt}
            error={
              hasOutstandingPenaltySoftBlock &&
              documentsTouched &&
              documentUrls.length === 0
                ? "Please upload at least one supporting document."
                : undefined
            }
          />

          {selectedFiles.length > 0 && (
            <div className="flex flex-col gap-y-3">
              <div className="flex flex-row gap-x-3 gap-y-3 flex-wrap">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between bg-baby-blue border border-navy-blue rounded-lg px-4 py-2.5 gap-x-3 flex-1 min-w-[200px] max-w-[calc(50%-6px)]"
                  >
                    <div className="flex items-center gap-x-2 flex-1 min-w-0">
                      <img
                        src="/assets/icons/document-solid.svg"
                        alt="document icon"
                        className="w-4 h-4 flex-shrink-0"
                      />
                      <p className="font-clash-display text-xs text-medium-dark-grey font-medium truncate">
                        {file.name}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFileRemoveAt(index)}
                      disabled={isUploadingFiles || uploadingFileIndex !== null}
                      className="flex items-center justify-center w-5 h-5 flex-shrink-0 hover:opacity-70 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Remove file"
                    >
                      <img
                        src="/assets/icons/delete.svg"
                        alt="delete"
                        className="w-4 h-4"
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <form.Subscribe
        children={(state) => {
          const isCommentInvalid =
            hasSoftBlock &&
            (!state.values.comment || state.values.comment.length < 10);
          const isDocumentsInvalid =
            hasOutstandingPenaltySoftBlock && documentUrls.length === 0;
          const isDisabled =
            !canProceed ||
            isCommentInvalid ||
            isDocumentsInvalid ||
            isSavingComplianceEvaluation ||
            isUploadingFiles ||
            uploadingFileIndex !== null;
          const title =
            uploadingFileIndex !== null
              ? "Uploading Files..."
              : isSavingComplianceEvaluation
              ? "Submitting..."
              : "Submit";
          return (
            <PrimaryButton
              className="mt-10 w-full"
              title={title}
              onClick={() => form.handleSubmit()}
              disabled={isDisabled}
              isLoading={isSavingComplianceEvaluation || isUploadingFiles}
            />
          );
        }}
      />
    </div>
  );
};
